import { UserPlus, X, PawPrint } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { CashierLabels } from "./types";

export interface CashierCustomer {
  id: string;
  name: string;
  petId?: string | null;
  petName?: string | null;
}

export interface CashierCustomerPickerProps {
  currentCustomer: CashierCustomer | null;
  customers?: readonly CashierCustomer[];
  onSelect?: (c: CashierCustomer) => void;
  onClear?: () => void;
  onCreateNew?: () => void;
  labels: CashierLabels;
}

export function CashierCustomerPicker({
  currentCustomer, customers = [], onClear, onCreateNew, labels, onSelect,
}: CashierCustomerPickerProps) {
  if (currentCustomer) {
    return (
      <div className="flex items-center gap-2 rounded-biz-btn border border-biz-primary/30 bg-biz-primary-subtle px-3 py-2">
        <div className="h-9 w-9 rounded-full bg-biz-primary text-biz-primary-foreground flex items-center justify-center text-sm font-semibold">
          {currentCustomer.name.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-biz-foreground truncate">{currentCustomer.name}</p>
          {currentCustomer.petName && (
            <p className="text-xs text-biz-foreground-muted flex items-center gap-1 truncate">
              <PawPrint className="h-3 w-3" />
              {currentCustomer.petName}
            </p>
          )}
        </div>
        {onClear && (
          <button
            type="button"
            aria-label={labels.cart.guest}
            onClick={onClear}
            className="h-9 w-9 rounded-biz-btn text-biz-foreground-muted hover:text-biz-danger hover:bg-biz-danger-subtle transition-colors biz-focus-ring flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <select
        aria-label={labels.cart.customerPick}
        defaultValue=""
        onChange={(event) => {
          const selected = customers.find((customer) => customer.id === event.target.value);
          if (selected) onSelect?.(selected);
        }}
        className={cn(
          "flex-1 h-11 rounded-biz-btn border border-dashed border-biz-border bg-biz-surface px-3",
          "text-sm font-semibold text-biz-foreground-muted",
          "hover:border-biz-primary hover:text-biz-primary hover:bg-biz-primary-subtle/40",
          "transition-colors biz-focus-ring",
        )}
      >
        <option value="">{labels.cart.customerPick}</option>
        {customers.map((customer) => (
          <option key={customer.id} value={customer.id}>
            {customer.name}
            {customer.petName ? ` · ${customer.petName}` : ""}
          </option>
        ))}
      </select>
      {onCreateNew && (
        <button
          type="button"
          aria-label={labels.cart.customerPick}
          onClick={onCreateNew}
          className="h-11 w-11 rounded-biz-btn border border-biz-border bg-biz-surface text-biz-foreground-muted hover:bg-biz-muted transition-colors biz-focus-ring flex items-center justify-center"
        >
          <UserPlus className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
